import { analyticsDate } from '@shared/lib/analytics-period'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { useEffect, useRef, useState } from 'react'
import Decimal from 'decimal.js'
import { createPortal } from 'react-dom'
import { Ban, Globe2, ShieldAlert, ShieldCheck } from 'lucide-react'
import {
  NavigationLink,
  Button,
  KeyValueGrid,
  DetailHero,
  Drawer,
  DrawerTabs,
  ErrorState,
  MoneyValue,
  MoneyReconciliation,
  MoneyRoundingNote,
  Skeleton,
  StatusBadge,
  TechnicalDetails,
} from '@shared/design-system/ui'
import { formatMoney } from '@shared/lib/format/currency'
import { formatPercentageLabel } from '@shared/lib/format/percent'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { Money } from '@shared/lib/money/money'
import type { MonetaryValue } from '@shared/lib/money/money'
import {
  negateMoney,
  roundMoneyForDisplay,
  sumMoneyParts,
} from '@shared/lib/money/display-reconciliation'
import { toast } from '@shared/lib/toast/toast-store'
import {
  buildJourneyFinancials,
  saleDetailJourney,
  summarizeJourney,
  summarizeJourneyStatuses,
  summarizeJourneyParticipantResult,
  getChargeStatusPresentation,
  getJourneyStatusPresentation,
  saleIdLabel,
  lineKindLabel,
  participantCommissionLabel,
  paymentMethodLabel,
  useRequestSaleRefundMutation,
  useSaleDetailQuery,
} from '@entities/sale'
import type {
  JourneyDeduction,
  SaleDetail,
  SaleFinancialEvent,
  SaleFinancialEventKind,
} from '@entities/sale'
import {
  chargeTag,
  deduction,
  debtApplicationBody,
  debtApplicationNote,
  debtApplicationTitle,
  detail as detailClass,
  detailBadgeRow,
  detailOffer,
  divider,
  itemAside,
  line,
  lineHint,
  lineLabel,
  lineValue,
  globalMarketTag,
  manualRefundCopy,
  manualRefundNotice,
  metaGrid,
  metaItem,
  metaLabel,
  metaValue,
  netLabel,
  netLead,
  netNote,
  netRow,
  netValue,
  refundDebtAlert,
  refundModalActions,
  refundModalBackdrop,
  refundModalBody,
  refundModalField,
  refundModalHeader,
  refundModalIrreversible,
  refundModalLabel,
  refundModalPanel,
  refundModalSummary,
  refundModalSummaryItem,
  refundModalSummaryTerm,
  refundModalSummaryValue,
  refundModalTextarea,
  refundModalTitle,
  refundModalTotal,
  refundModalTotalLabel,
  refundModalTotalValue,
  refundCard,
  refundHead,
  refundScopeNote,
  refundText,
  refundTitle,
  reserveNote,
  reserveNoteAmount,
  reserveNoteIcon,
  section,
  sectionNote,
  sectionTitle,
  splitLead,
  splitMeta,
  splitName,
  splitRow,
} from './sales.css'

type SaleDetailDrawerProps = {
  scope?: 'journey' | 'charge'
  saleId: string | null
  onClose: () => void
  onBack?: () => void
  backLabel?: string
}

type SaleRefundTarget = {
  saleId: string
  label: string
  productName: string
  status: SaleDetail['status']
  amount: SaleDetail['amount']
  grossAmount: SaleDetail['grossAmount']
  createdAt: string
  availableAt?: string
  canRequestRefund: boolean
  handledByGlobalChargeback?: boolean
  financialEvents?: SaleFinancialEvent[]
  subscriptionId?: string
  subscriptionStatus?: SaleDetail['subscriptionStatus']
  refundAmount: SaleDetail['amount']
  refundGrossAmount: SaleDetail['grossAmount']
  items: Array<{
    label: string
    productName: string
    amount: SaleDetail['grossAmount']
  }>
}

function remainingRefundAmounts(
  amount: SaleDetail['amount'],
  grossAmount: SaleDetail['grossAmount'],
  events: SaleFinancialEvent[] | undefined,
) {
  const partial = [...(events ?? [])].reverse().find((event) => event.kind === 'PARTIAL_REFUND')
  const affectedAmount = partial?.chargeAffectedAmount ?? partial?.affectedAmount
  if (!affectedAmount) return { amount, grossAmount }

  const buyerOriginal = Money.fromMonetaryValue(amount)
  const grossOriginal = Money.fromMonetaryValue(grossAmount)
  const affected = Money.fromMonetaryValue(affectedAmount)
  const reference =
    affected.currency === grossOriginal.currency
      ? grossOriginal
      : affected.currency === buyerOriginal.currency
        ? buyerOriginal
        : null
  if (!reference) return { amount, grossAmount }
  const referenceAmount = new Decimal(reference.toAmountString())
  if (referenceAmount.lte(0)) return { amount, grossAmount }

  const remainingRatio = Decimal.max(
    0,
    Decimal.min(1, referenceAmount.minus(affected.toAmountString()).div(referenceAmount)),
  )
  return {
    amount: buyerOriginal.percent(remainingRatio.mul(100).toString()).toMonetaryValue(),
    grossAmount: grossOriginal.percent(remainingRatio.mul(100).toString()).toMonetaryValue(),
  }
}

function refundTargetsFor(sale: SaleDetail): SaleRefundTarget[] {
  const parentItemCount = Math.max(sale.lines?.length ?? 1, 1)
  const parentLabel = sale.isRenewal
    ? `Renovação${sale.subscriptionCycleN ? ` · ciclo ${sale.subscriptionCycleN}` : ''}`
    : (sale.funnelStages?.length ?? 0) > 0
      ? 'Cobrança inicial'
      : 'Cobrança da venda'
  const parentProduct =
    parentItemCount > 1
      ? `${sale.productName} + ${parentItemCount - 1} ${parentItemCount === 2 ? 'item adicional' : 'itens adicionais'}`
      : sale.productName

  const parentEvents = sale.financialEvents
  const parentRemaining = remainingRefundAmounts(sale.amount, sale.grossAmount, parentEvents)
  const parentSourceItems =
    sale.lines && sale.lines.length > 0
      ? sale.lines.map((line) => ({
          label: lineKindLabel(line.kind),
          productName: line.productName,
          amount: line.amount,
        }))
      : [
          {
            label: lineKindLabel('PRINCIPAL'),
            productName: sale.productName,
            amount: sale.grossAmount,
          },
        ]
  const parentItemAmounts = parentSourceItems.map((item) => roundMoneyForDisplay(item.amount))
  const parentItems = parentSourceItems.map((item, index) => ({
    ...item,
    amount: parentItemAmounts[index] ?? roundMoneyForDisplay(item.amount),
  }))
  return [
    {
      saleId: sale.id,
      label: parentLabel,
      productName: parentProduct,
      status: sale.status,
      amount: sale.amount,
      grossAmount: sale.grossAmount,
      createdAt: sale.createdAt,
      availableAt: sale.availableAt,
      canRequestRefund: sale.permissions?.canRequestRefund === true,
      handledByGlobalChargeback: sale.handledByGlobalChargeback,
      financialEvents: parentEvents,
      subscriptionId: sale.subscriptionId,
      subscriptionStatus: sale.subscriptionStatus,
      refundAmount: parentRemaining.amount,
      refundGrossAmount: parentRemaining.grossAmount,
      items: parentItems,
    },
    ...(sale.funnelStages ?? []).map((stage): SaleRefundTarget => {
      const remaining = remainingRefundAmounts(
        stage.amount,
        stage.grossAmount,
        stage.financialEvents,
      )
      return {
        saleId: stage.saleId,
        label: lineKindLabel(stage.kind),
        productName: stage.productName,
        status: stage.status,
        amount: stage.amount,
        grossAmount: stage.grossAmount,
        createdAt: stage.createdAt ?? sale.createdAt,
        availableAt: stage.availableAt,
        canRequestRefund: stage.permissions?.canRequestRefund === true,
        handledByGlobalChargeback: stage.handledByGlobalChargeback,
        financialEvents: stage.financialEvents,
        subscriptionId: stage.subscriptionId,
        subscriptionStatus: stage.subscriptionStatus,
        refundAmount: remaining.amount,
        refundGrossAmount: remaining.grossAmount,
        items: [
          {
            label: lineKindLabel(stage.kind),
            productName: stage.productName,
            amount: stage.grossAmount,
          },
        ],
      }
    }),
  ]
}

function refundActionLabel(target: SaleRefundTarget, targets: readonly SaleRefundTarget[]): string {
  const base = `Reembolsar ${target.label.toLocaleLowerCase('pt-BR')}`
  const sameLabelTargets = targets.filter((candidate) => candidate.label === target.label)
  if (sameLabelTargets.length === 1) return base
  const position = sameLabelTargets.findIndex((candidate) => candidate.saleId === target.saleId) + 1
  return `${base} ${position}: ${target.productName}`
}

function financialEventNetNote(kind: SaleFinancialEventKind): string {
  if (kind === 'PARTIAL_REFUND') return 'Reembolso parcial. Confira os lançamentos no extrato.'
  if (kind === 'DISPUTE_WON') return 'Disputa ganha. Confira as devoluções no extrato.'
  return 'Confira os ajustes no extrato.'
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso))
}

const refundReasonMaxLength = 500

function sellerDeductions(items: JourneyDeduction[]): JourneyDeduction[] {
  const charges = items.filter((item) => item.type === 'fee' || item.type === 'currencyConversion')
  const first = charges[0]
  if (!first) return items
  const total = charges.reduce(
    (value, item) => value.add(Money.fromMonetaryValue(item.amount)),
    new Money('0', first.amount.currency),
  )
  return [
    {
      ...first,
      type: 'fee',
      amount: { amount: total.toAmountString(), currency: first.amount.currency },
    },
    ...items.filter((item) => item.type !== 'fee' && item.type !== 'currencyConversion'),
  ]
}

function deductionLabel(item: JourneyDeduction): string {
  if (item.type === 'fee') {
    return 'Serviço VendePay'
  }
  if (item.type === 'currencyConversion') {
    return 'Taxa de conversão'
  }
  if (item.type === 'participant' && item.role) {
    return participantCommissionLabel(item.role)
  }
  return `${lineKindLabel(item.kind ?? 'UPSELL')} · você não comissionou`
}

function embeddedUncommissionedLabel(
  fin: ReturnType<typeof buildJourneyFinancials>,
): string | null {
  const labels = Array.from(
    new Set(fin.embeddedUncommissionedItems.map((item) => lineKindLabel(item.kind))),
  )
  if (labels.length === 0) {
    return null
  }
  return `Inclui ${labels.join(', ')} sem comissão`
}

function maskEmail(email: string): string {
  const [local = '', domain = ''] = email.split('@')
  if (!domain) {
    return email
  }
  const visible = local.length <= 2 ? local.slice(0, 1) : local.slice(0, 2)
  return `${visible}***@${domain}`
}

function buyerSummary(sale: SaleDetail): string {
  return sale.buyerEmail ? maskEmail(sale.buyerEmail) : sale.buyerName
}

function isPastDate(iso?: string): boolean {
  if (!iso) {
    return false
  }
  const timestamp = new Date(iso).getTime()
  return Number.isFinite(timestamp) && timestamp < Date.now()
}

function billingTypeLabel(type: 'ONE_TIME' | 'RECURRING'): string {
  return type === 'RECURRING' ? 'Recorrente' : 'Pagamento único'
}

function couponTypeLabel(type: NonNullable<SaleDetail['coupon']>['type']): string {
  return type === 'fixed' ? 'Fixo' : 'Percentual'
}

function couponRuleLabel(coupon: NonNullable<SaleDetail['coupon']>): string {
  if (coupon.type === 'percent' && coupon.percent !== undefined) {
    return `${coupon.percent}%`
  }
  return couponTypeLabel(coupon.type)
}

function vendepayFeeWithoutConversion(item: {
  platformFee?: MonetaryValue
  currencyConversionFee?: MonetaryValue
}): MonetaryValue | undefined {
  if (!item.platformFee) return undefined
  if (
    !item.currencyConversionFee ||
    item.platformFee.currency !== item.currencyConversionFee.currency
  ) {
    return item.platformFee
  }
  return Money.fromMonetaryValue(item.platformFee)
    .subtract(Money.fromMonetaryValue(item.currencyConversionFee))
    .toMonetaryValue()
}

type JourneyChargeForDisplay = {
  amount: MonetaryValue
  grossAmount: MonetaryValue
  platformFee: MonetaryValue
  currencyConversionFee?: MonetaryValue
  reserveAmount: MonetaryValue
  netAmount: MonetaryValue
  split: SaleDetail['split']
  debtApplication?: SaleDetail['debtApplication']
}

function sumRoundedChargeValues(
  values: readonly MonetaryValue[],
  currency: MonetaryValue['currency'],
  fallback: MonetaryValue,
): MonetaryValue {
  // Each charge is settled separately, so journey totals must add the same values shown per charge.
  const total = sumMoneyParts(values.map(roundMoneyForDisplay), currency)
  return total ? { amount: total.amount, currency } : roundMoneyForDisplay(fallback)
}

function financialsForDisplay(raw: ReturnType<typeof buildJourneyFinancials>, sale: SaleDetail) {
  const optional = (value: MonetaryValue | undefined) =>
    value ? roundMoneyForDisplay(value) : undefined
  const receivingCurrency = sale.grossAmount.currency
  const buyerCurrency = sale.amount.currency
  const charges: JourneyChargeForDisplay[] = [sale, ...(sale.funnelStages ?? [])]
  const commissionedCharges = charges.filter(
    (charge) =>
      !Money.fromMonetaryValue(charge.netAmount)
        .add(Money.fromMonetaryValue(charge.reserveAmount))
        .isZero(),
  )
  const totalGross = sumRoundedChargeValues(
    charges.map((charge) => charge.grossAmount),
    receivingCurrency,
    raw.totalGross,
  )
  const totalPaid = sumRoundedChargeValues(
    charges.map((charge) => charge.amount),
    buyerCurrency,
    raw.totalPaid,
  )
  const availableNow = sumRoundedChargeValues(
    commissionedCharges.map((charge) => charge.netAmount),
    receivingCurrency,
    raw.availableNow,
  )
  const reserveHeld = sumRoundedChargeValues(
    commissionedCharges.map((charge) => charge.reserveAmount),
    receivingCurrency,
    raw.reserveHeld,
  )
  const netTotal = sumRoundedChargeValues(
    commissionedCharges.map((charge) =>
      Money.fromMonetaryValue(charge.netAmount)
        .add(Money.fromMonetaryValue(charge.reserveAmount))
        .toMonetaryValue(),
    ),
    receivingCurrency,
    raw.netTotal,
  )
  const debtConsumed = sumRoundedChargeValues(
    commissionedCharges.flatMap((charge) =>
      charge.debtApplication
        ? [
            charge.debtApplication.consumedReceivableAmount,
            charge.debtApplication.consumedReserveAmount,
          ]
        : [],
    ),
    receivingCurrency,
    raw.debtConsumed,
  )
  const remainingReceivable = sumRoundedChargeValues(
    commissionedCharges.map(
      (charge) => charge.debtApplication?.remainingReceivableAmount ?? charge.netAmount,
    ),
    receivingCurrency,
    raw.remainingReceivable,
  )
  const remainingReserve = sumRoundedChargeValues(
    commissionedCharges.map(
      (charge) => charge.debtApplication?.remainingReserveAmount ?? charge.reserveAmount,
    ),
    receivingCurrency,
    raw.remainingReserve,
  )

  return {
    ...raw,
    totalPaid,
    totalGross,
    netTotal,
    availableNow,
    reserveHeld,
    debtConsumed,
    remainingReceivable,
    remainingReserve,
    items: raw.items.map((item) => ({
      ...item,
      amount: roundMoneyForDisplay(item.amount),
      platformFee: optional(vendepayFeeWithoutConversion(item)),
      currencyConversionFee: optional(item.currencyConversionFee),
      fixedFee: optional(item.fixedFee),
      reserveAmount: optional(item.reserveAmount),
      netAmount: optional(item.netAmount),
    })),
    deductions: raw.deductions.map((item) => {
      if (item.type === 'fee') {
        const fees = commissionedCharges
          .map(vendepayFeeWithoutConversion)
          .filter((value): value is MonetaryValue => value !== undefined)
        return {
          ...item,
          amount: sumRoundedChargeValues(fees, receivingCurrency, item.amount),
        }
      }
      if (item.type === 'currencyConversion') {
        const fees = commissionedCharges
          .map((charge) => charge.currencyConversionFee)
          .filter((value): value is MonetaryValue => value !== undefined)
        return {
          ...item,
          amount: sumRoundedChargeValues(fees, receivingCurrency, item.amount),
        }
      }
      if (item.type === 'participant' && item.role) {
        const parts = commissionedCharges.flatMap((charge) =>
          charge.split.filter((part) => !part.isYou && part.role === item.role),
        )
        return {
          ...item,
          amount: sumRoundedChargeValues(
            parts.map((part) => part.amount),
            receivingCurrency,
            item.amount,
          ),
          available: item.available
            ? sumRoundedChargeValues(
                parts.map((part) => part.available ?? part.amount),
                receivingCurrency,
                item.available,
              )
            : undefined,
          reserve: item.reserve
            ? sumRoundedChargeValues(
                parts.map(
                  (part) => part.reserve ?? { amount: '0.000', currency: receivingCurrency },
                ),
                receivingCurrency,
                item.reserve,
              )
            : undefined,
        }
      }
      return {
        ...item,
        amount: roundMoneyForDisplay(item.amount),
        available: optional(item.available),
        reserve: optional(item.reserve),
      }
    }),
  }
}

function CouponSummarySection({ sale }: { sale: SaleDetail }) {
  if (!sale.coupon) return null

  const { coupon } = sale

  return (
    <div className={section} aria-label="Cupom aplicado">
      <span className={sectionTitle}>Cupom aplicado</span>
      <div className={line}>
        <span className={lineLabel}>Código</span>
        <span className={lineValue}>{coupon.code}</span>
      </div>
      <div className={line}>
        <span className={lineLabel}>Regra</span>
        <span className={lineValue}>{couponRuleLabel(coupon)}</span>
      </div>
      {coupon.configuredAmount ? (
        <div className={line}>
          <span className={lineLabel}>Valor configurado</span>
          <span className={lineValue}>
            <MoneyValue
              amount={coupon.configuredAmount.amount}
              currency={coupon.configuredAmount.currency}
              size="sm"
            />
          </span>
        </div>
      ) : null}
      <div className={line}>
        <span className={lineLabel}>Valor antes do desconto</span>
        <span className={lineValue}>
          <MoneyValue
            amount={coupon.amountBeforeDiscount.amount}
            currency={coupon.amountBeforeDiscount.currency}
            size="sm"
          />
        </span>
      </div>
      <div className={line}>
        <span className={lineLabel}>Desconto aplicado</span>
        <span className={lineValue}>
          <MoneyValue
            amount={coupon.discountAmount.amount}
            currency={coupon.discountAmount.currency}
            size="sm"
          />
        </span>
      </div>
      {coupon.settlement ? (
        <>
          <div className={line}>
            <span className={lineLabel}>Valor antes do desconto na liquidação</span>
            <span className={lineValue}>
              <MoneyValue
                amount={coupon.settlement.amountBeforeDiscount.amount}
                currency={coupon.settlement.amountBeforeDiscount.currency}
                size="sm"
              />
            </span>
          </div>
          <div className={line}>
            <span className={lineLabel}>Desconto na liquidação</span>
            <span className={lineValue}>
              <MoneyValue
                amount={coupon.settlement.discountAmount.amount}
                currency={coupon.settlement.discountAmount.currency}
                size="sm"
              />
            </span>
          </div>
        </>
      ) : null}
    </div>
  )
}

export function SaleDetailBody({
  sale,
  activeTab = 'all',
  onRequestRefund,
  requestingRefund = false,
}: {
  sale: SaleDetail
  activeTab?: 'all' | 'summary' | 'composition'
  onRequestRefund?: (target: SaleRefundTarget) => void
  requestingRefund?: boolean
}) {
  const customServiceFees = useFeatureFlag('customServiceFees')
  const [showRefundTargets, setShowRefundTargets] = useState(false)
  const isFailed = sale.status === 'FAILED'
  const originalFinancials = buildJourneyFinancials(sale)
  const fin = financialsForDisplay(originalFinancials, sale)
  const refund = sale.refund
  const hasDebtConsumption = fin.hasDebtConsumption
  const hasRemainingReceivable = Number(fin.remainingReceivable.amount) > 0
  const remainingReserve = fin.remainingReserve
  const hasRemainingReserve = Number(remainingReserve.amount) > 0
  const debtConsumedAmount = fin.debtConsumed
  const hasDebtConsumedAmount = Number(debtConsumedAmount.amount) > 0
  const embeddedLabel = embeddedUncommissionedLabel(fin)
  const refundTargets = refundTargetsFor(sale)
  const refundableTargets = refundTargets.filter((target) => target.canRequestRefund)
  const occurrenceTargets = refundTargets.filter(
    (target) =>
      (target.financialEvents?.length ?? 0) > 0 || target.subscriptionStatus === 'CANCELED',
  )
  const journey = saleDetailJourney(sale)
  const summary = summarizeJourney(journey)
  const impact = {
    active: summary.total,
    original: summary.originalTotal,
    affected: summary.affectedTotal,
    hasImpact: summary.hasFinancialImpact,
  }
  const participantResult = summarizeJourneyParticipantResult(sale)
  const hasRecordedEvents = [sale, ...(sale.funnelStages ?? [])].some(
    (charge) => (charge.financialEvents?.length ?? 0) > 0,
  )
  const hasFinancialImpact = impact.hasImpact
  const refundPending = refundTargets.some((target) => target.status === 'REFUND_REQUESTED')
  const primaryFinancialEvent = sale.financialEvents
    ?.filter((event) => event.kind !== 'FX_LOSS')
    .at(-1)

  return (
    <div className={detailClass}>
      {activeTab === 'summary' || activeTab === 'all' ? (
        <DetailHero
          eyebrow={hasFinancialImpact ? 'Bruto que continua válido' : 'Valor da compra'}
          value={
            <MoneyValue
              amount={(hasFinancialImpact ? impact.active : fin.totalGross).amount}
              currency={(hasFinancialImpact ? impact.active : fin.totalGross).currency}
              size="lg"
            />
          }
          description={
            isFailed
              ? 'Pagamento não aprovado. Nada foi cobrado e nenhum valor foi liberado.'
              : refundPending
                ? 'Reembolso solicitado. Aguardando confirmação do processamento.'
                : hasFinancialImpact
                  ? Number(impact.active.amount) === 0
                    ? 'Nenhum valor desta compra continua válido após o reembolso ou contestação.'
                    : 'Parte da jornada foi reembolsada ou contestada.'
                  : 'Pagamento aprovado e cobrança válida.'
          }
          tone={isFailed ? 'danger' : hasFinancialImpact || refundPending ? 'warning' : 'success'}
          aside={
            <span>
              {hasFinancialImpact ? 'Reembolsado ou contestado' : 'Seu líquido'}
              <br />
              <strong>
                {formatMoney(
                  (hasFinancialImpact ? impact.affected : fin.netTotal).amount,
                  (hasFinancialImpact ? impact.affected : fin.netTotal).currency,
                )}
              </strong>
            </span>
          }
        />
      ) : null}

      {(activeTab === 'composition' || activeTab === 'all') &&
      !isFailed &&
      (hasFinancialImpact || hasRecordedEvents) ? (
        <section className={section} aria-label="Seu resultado após ajustes">
          <span className={sectionTitle}>Seu resultado após ajustes</span>
          {participantResult.adjusted ? (
            <>
              <MoneyValue
                amount={participantResult.adjusted.amount}
                currency={participantResult.adjusted.currency}
                size="lg"
              />
              <KeyValueGrid
                items={[
                  {
                    label: 'Seu líquido original, com reserva',
                    value: formatMoney(
                      participantResult.original.amount,
                      participantResult.original.currency,
                    ),
                  },
                  {
                    label: 'Sua parte nos reembolsos e contestações',
                    value: formatMoney(
                      participantResult.losses.amount,
                      participantResult.losses.currency,
                    ),
                  },
                  {
                    label: 'Sua parte na perda cambial',
                    value: formatMoney(
                      participantResult.fxLosses.amount,
                      participantResult.fxLosses.currency,
                    ),
                  },
                  {
                    label: 'Multas já descontadas de você',
                    value: formatMoney(
                      participantResult.paidFees.amount,
                      participantResult.paidFees.currency,
                    ),
                  },
                ]}
              />
              <span className={sectionNote}>
                Líquido original menos sua parte nos reembolsos, contestações, perdas cambiais e
                multas já descontadas. Inclui reserva; não é o saldo disponível para saque.
              </span>
            </>
          ) : (
            <span className={sectionNote}>
              Os dados desta compra não permitem apurar seu resultado completo. Consulte os
              lançamentos no extrato.
            </span>
          )}
        </section>
      ) : null}

      {activeTab === 'summary' || activeTab === 'all' ? (
        <div className={metaGrid}>
          <div className={metaItem}>
            <span className={metaLabel}>ID da venda</span>
            <span className={metaValue} title={sale.id}>
              {saleIdLabel(sale.id)}
            </span>
            <span className={detailOffer}>{sale.id}</span>
          </div>
          <div className={metaItem}>
            <span className={metaLabel}>Comprador</span>
            <span className={metaValue}>{sale.buyerName}</span>
            {sale.buyerEmail ? <span className={detailOffer}>{sale.buyerEmail}</span> : null}
          </div>
          <div className={metaItem}>
            <span className={metaLabel}>Data</span>
            <span className={metaValue}>{formatDate(sale.createdAt)}</span>
          </div>
          {sale.availability?.cardCountry ? (
            <div className={metaItem}>
              <span className={metaLabel}>País de emissão do cartão</span>
              <span className={metaValue}>{sale.availability.cardCountry}</span>
            </div>
          ) : null}
          {sale.paymentMethod ? (
            <div className={metaItem}>
              <span className={metaLabel}>Pagamento</span>
              <span className={metaValue}>
                {paymentMethodLabel(sale.paymentMethod)}
                {sale.installments && sale.installments > 1 ? ` · ${sale.installments}x` : ''}
              </span>
            </div>
          ) : null}
          {sale.receivingPlan?.kind === 'CUSTOM' ? (
            <div className={metaItem}>
              <span className={metaLabel}>Plano de recebimento</span>
              <span className={metaValue}>Personalizado</span>
            </div>
          ) : null}
          {sale.globalMarket ? (
            <div className={metaItem}>
              <span className={metaLabel}>Mercado</span>
              <span className={metaValue}>Mercado Global</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {activeTab === 'summary' || activeTab === 'all' ? (
        <NavigationLink
          href={`/financas?currency=${sale.netAmount.currency}&q=${encodeURIComponent(sale.id)}`}
        >
          Ver lançamentos no extrato
        </NavigationLink>
      ) : null}

      {activeTab === 'summary' || activeTab === 'all' ? (
        <TechnicalDetails
          title="Referências da venda"
          rows={[
            { label: 'ID da venda', value: sale.id },
            ...(sale.originSaleId && sale.originSaleId !== sale.id
              ? [{ label: 'Jornada', value: sale.originSaleId }]
              : []),
          ]}
        />
      ) : null}

      {activeTab === 'summary' || activeTab === 'all' ? <CouponSummarySection sale={sale} /> : null}

      {(activeTab === 'summary' || activeTab === 'all') && occurrenceTargets.length > 0 ? (
        <div className={section} aria-label="Situação das cobranças">
          <span className={sectionTitle}>Situação das cobranças</span>
          {occurrenceTargets.map((target) => {
            const status = getChargeStatusPresentation(target)
            return (
              <div className={splitRow} key={target.saleId}>
                <div className={splitLead}>
                  <span className={splitName}>{target.label}</span>
                  <span className={splitMeta}>{target.productName}</span>
                  <NavigationLink
                    href={`/financas?currency=BRL&q=${encodeURIComponent(target.saleId)}`}
                  >
                    Ver no extrato
                  </NavigationLink>
                </div>
                <StatusBadge tone={status.tone} label={status.label} size="sm" />
              </div>
            )
          })}
        </div>
      ) : null}

      {(activeTab === 'summary' || activeTab === 'all') && isFailed ? (
        <div className={refundCard({ tone: 'informative' })}>
          <div className={refundHead}>
            <Ban size={16} />
            <span className={refundTitle}>Pagamento não aprovado</span>
          </div>
          <span className={refundText}>
            A cobrança não foi concluída. Nada foi cobrado do comprador e nada foi liberado para
            você.
          </span>
        </div>
      ) : null}

      {(activeTab === 'summary' || activeTab === 'all') &&
      refundableTargets.length > 0 &&
      onRequestRefund ? (
        <>
          <div className={manualRefundNotice}>
            <div className={manualRefundCopy}>
              <span className={refundTitle}>Reembolso</span>
              <span className={refundText}>
                {refundTargets.length > 1
                  ? `Esta venda tem ${refundTargets.length} cobranças. Escolha qual deseja reembolsar.`
                  : refundTargets[0]?.financialEvents?.some(
                        (event) => event.kind === 'PARTIAL_REFUND',
                      )
                    ? 'Você pode reembolsar o valor restante desta cobrança.'
                    : 'Reembolso total desta cobrança pelo gateway.'}
              </span>
            </div>
            <Button
              variant="text"
              size="sm"
              disabled={requestingRefund}
              aria-expanded={refundTargets.length > 1 ? showRefundTargets : undefined}
              aria-label={
                refundTargets.length > 1
                  ? showRefundTargets
                    ? 'Ocultar cobranças para reembolso'
                    : 'Escolher cobrança para reembolso'
                  : 'Solicitar reembolso'
              }
              onClick={() => {
                if (refundTargets.length === 1) {
                  onRequestRefund(refundableTargets[0]!)
                  return
                }
                setShowRefundTargets((current) => !current)
              }}
            >
              {refundTargets.length > 1
                ? showRefundTargets
                  ? 'Ocultar'
                  : 'Escolher cobrança'
                : 'Solicitar'}
            </Button>
          </div>
          {showRefundTargets ? (
            <div className={section} aria-label="Cobranças disponíveis para reembolso">
              <span className={sectionTitle}>Reembolso por cobrança</span>
              <div>
                {refundTargets.map((target) => {
                  const targetPresentation = getChargeStatusPresentation(target)
                  const hasPartialRefund = target.financialEvents?.some(
                    (event) => event.kind === 'PARTIAL_REFUND',
                  )
                  return (
                    <div className={splitRow} key={target.saleId}>
                      <div className={splitLead}>
                        <span className={splitName}>{target.label}</span>
                        <span className={splitMeta}>{target.productName}</span>
                        {hasPartialRefund && target.canRequestRefund ? (
                          <span className={splitMeta}>Valor restante para reembolso</span>
                        ) : null}
                      </div>
                      <div className={itemAside}>
                        <StatusBadge
                          tone={targetPresentation.tone}
                          label={targetPresentation.label}
                          size="sm"
                        />
                        <MoneyValue
                          amount={target.refundAmount.amount}
                          currency={target.refundAmount.currency}
                          size="sm"
                        />
                        {target.canRequestRefund ? (
                          <Button
                            variant="text"
                            size="sm"
                            disabled={requestingRefund}
                            aria-label={refundActionLabel(target, refundTargets)}
                            onClick={() => onRequestRefund(target)}
                          >
                            Reembolsar
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {isFailed || (activeTab !== 'composition' && activeTab !== 'all') ? null : (
        <div className={section}>
          <span className={sectionTitle}>Itens da venda</span>
          {fin.commercialAmount ? (
            <span className={sectionNote}>
              {fin.items.length === 1
                ? 'Preço contratado. A variação cambial está discriminada abaixo.'
                : 'Valores liquidados por item. O preço contratado e a variação cambial total estão discriminados abaixo.'}
            </span>
          ) : null}
          <div>
            {fin.items.map((item, index) => {
              const stagePresentation =
                item.separateCharge &&
                (item.status ||
                  (item.financialEvents?.length ?? 0) > 0 ||
                  item.subscriptionStatus === 'CANCELED')
                  ? getChargeStatusPresentation({
                      status: item.status ?? 'PAID',
                      handledByGlobalChargeback: item.handledByGlobalChargeback,
                      financialEvents: item.financialEvents,
                      subscriptionStatus: item.subscriptionStatus,
                    })
                  : null
              return (
                <div className={splitRow} key={`${item.kind}-${index}`}>
                  <div className={splitLead}>
                    <span className={splitName}>
                      {item.productName}
                      {item.offerName && item.offerName !== item.productName
                        ? ` · ${item.offerName}`
                        : ''}
                      {item.separateCharge ? (
                        <span className={chargeTag}>cobrança separada</span>
                      ) : null}
                    </span>
                    <span className={splitMeta}>
                      {lineKindLabel(item.kind)}
                      {item.separateCharge
                        ? ' · cobrança separada'
                        : sale.isRenewal
                          ? sale.subscriptionCycleN
                            ? ` · renovação · ciclo ${sale.subscriptionCycleN}`
                            : ' · renovação'
                          : ' · cobrança inicial'}
                      {item.billingType ? ` · ${billingTypeLabel(item.billingType)}` : ''}
                      {item.youCommissioned === undefined
                        ? ''
                        : item.youCommissioned
                          ? ' · você comissionou'
                          : ' · você não comissionou'}
                    </span>
                  </div>
                  <div className={itemAside}>
                    {stagePresentation ? (
                      <StatusBadge
                        tone={stagePresentation.tone}
                        label={stagePresentation.label}
                        size="sm"
                      />
                    ) : null}
                    <MoneyValue
                      amount={
                        fin.items.length === 1 && fin.commercialAmount
                          ? fin.commercialAmount.amount
                          : item.amount.amount
                      }
                      currency={
                        fin.items.length === 1 && fin.commercialAmount
                          ? fin.commercialAmount.currency
                          : item.amount.currency
                      }
                      size="sm"
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {isFailed || (activeTab !== 'composition' && activeTab !== 'all') ? null : (
        <CouponSummarySection sale={sale} />
      )}

      {isFailed || (activeTab !== 'composition' && activeTab !== 'all') ? null : (
        <div className={section}>
          <span className={sectionTitle}>
            {hasFinancialImpact ? 'Valores e abatimentos' : 'Valores'}
          </span>
          {fin.commercialAmount && fin.fxVariation ? (
            <>
              <div className={line}>
                <span className={lineLabel}>
                  Preço contratado
                  <span className={lineHint}>Valor da compra em reais, após descontos</span>
                </span>
                <span className={lineValue}>
                  <MoneyValue {...fin.commercialAmount} size="sm" />
                </span>
              </div>
              <div className={line}>
                <span className={lineLabel}>
                  Variação cambial
                  <span className={lineHint}>
                    Diferença entre o preço contratado e o bruto desta cobrança
                  </span>
                </span>
                <span className={lineValue}>
                  <MoneyValue {...fin.fxVariation} size="sm" />
                </span>
              </div>
              <MoneyRoundingNote
                total={fin.totalGross}
                parts={[fin.commercialAmount, fin.fxVariation]}
              />
            </>
          ) : null}
          {hasFinancialImpact ? (
            <span className={sectionNote}>
              Taxas e líquido preservam a composição original. Recebível e reserva abaixo mostram o
              restante após abatimentos. Consulte Recebimentos para o saldo disponível agora.
            </span>
          ) : null}
          <div className={line}>
            <span className={lineLabel}>
              {fin.commercialAmount ? 'Bruto da cobrança' : 'Valor da venda'}
              {fin.converted ? (
                <span className={detailOffer}>
                  {' '}
                  · {formatMoney(fin.totalPaid.amount, fin.totalPaid.currency)}
                </span>
              ) : null}
            </span>
            <span className={lineValue}>
              <MoneyValue
                amount={fin.totalGross.amount}
                currency={fin.totalGross.currency}
                size="sm"
              />
            </span>
          </div>
          {sellerDeductions(fin.deductions).map((item) => (
            <div className={line} key={item.id}>
              <span className={lineLabel}>
                {deductionLabel(item)}
                {item.type === 'fee' && sale.serviceFeeQuote ? (
                  <>
                    <span className={lineHint} data-testid="sale-service-fee-formula">
                      {(sale.funnelStages?.length ?? 0) > 0 ? 'Cobrança principal: ' : ''}
                      {customServiceFees
                        ? formatPercentageLabel(sale.serviceFeeQuote.percentage)
                        : `${sale.serviceFeeQuote.percentage.replace('.', ',')}%`}{' '}
                      + US$ {sale.serviceFeeQuote.sourceAmount.replace('.', ',')}
                      {(sale.funnelStages?.length ?? 0) > 0
                        ? '. Cada cobrança mantém sua condição registrada.'
                        : ' por cobrança'}
                    </span>
                    {fin.fixedFeeCount !== undefined && fin.fixedFeeCount > 1 ? (
                      <span className={lineHint}>
                        {fin.fixedFeeCount} tarifas fixas neste total. Principal e order bump
                        compartilham uma tarifa; cada cobrança adicional tem a sua.
                      </span>
                    ) : null}
                  </>
                ) : null}
                {item.type === 'participant' &&
                item.reserve &&
                Number(item.reserve.amount) > 0 &&
                item.available ? (
                  <span className={detailOffer}>
                    {' '}
                    · disp {formatMoney(item.available.amount, item.available.currency)} · reserva{' '}
                    {formatMoney(item.reserve.amount, item.reserve.currency)}
                  </span>
                ) : null}
                {item.type === 'participant' &&
                item.role === 'PRODUCER' &&
                embeddedLabel !== null ? (
                  <span className={lineHint}>{embeddedLabel}</span>
                ) : null}
              </span>
              <span className={`${lineValue} ${deduction}`}>
                −{' '}
                <MoneyValue amount={item.amount.amount} currency={item.amount.currency} size="sm" />
              </span>
            </div>
          ))}
          <MoneyReconciliation
            total={originalFinancials.netTotal}
            parts={[
              originalFinancials.totalGross,
              ...originalFinancials.deductions.map((item) => negateMoney(item.amount)),
            ]}
          />
          <hr className={divider} />
          {hasRemainingReceivable ? (
            <div className={line}>
              <span className={lineLabel}>
                {hasFinancialImpact ? 'Recebível após abatimentos' : 'A liberar'}
                <span className={lineHint}>
                  {fin.availabilityConfirmed
                    ? 'Disponibilidade confirmada na conta de pagamento'
                    : fin.awaitingAvailability
                      ? 'Aguardando previsão de liberação'
                      : fin.availableDates.length === 1
                        ? `Liberação prevista: ${customServiceFees ? analyticsDate(fin.availableDates[0]!) : new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(new Date(fin.availableDates[0]!))}`
                        : fin.availableDates.length > 1
                          ? `Liberação em ${fin.availableDates.length} datas`
                          : 'Aguardando previsão de liberação'}
                </span>
              </span>
              <span className={lineValue}>
                <MoneyValue
                  amount={fin.remainingReceivable.amount}
                  currency={fin.remainingReceivable.currency}
                  size="sm"
                />
              </span>
            </div>
          ) : null}
          {hasRemainingReserve ? (
            <div className={line}>
              <span className={lineLabel}>
                {hasFinancialImpact ? 'Reserva após abatimentos' : 'Em reserva'}
              </span>
              <span className={lineValue}>
                <MoneyValue
                  amount={remainingReserve.amount}
                  currency={remainingReserve.currency}
                  size="sm"
                />
              </span>
            </div>
          ) : null}
          {hasDebtConsumedAmount ? (
            <div className={line}>
              <span className={lineLabel}>Abatido em dívida</span>
              <span className={lineValue}>
                <MoneyValue
                  amount={debtConsumedAmount.amount}
                  currency={debtConsumedAmount.currency}
                  size="sm"
                />
              </span>
            </div>
          ) : null}
          {hasRemainingReserve && !refund && !hasFinancialImpact ? (
            <div className={reserveNote}>
              <ShieldCheck size={17} className={reserveNoteIcon} />
              <span>
                Desse líquido,{' '}
                <strong className={reserveNoteAmount}>
                  {formatMoney(remainingReserve.amount, remainingReserve.currency)}
                </strong>{' '}
                fica retido temporariamente como reserva de segurança. Acompanhe a liberação em
                Recebimentos.
              </span>
            </div>
          ) : null}
          {hasDebtConsumption && hasDebtConsumedAmount && !hasFinancialImpact ? (
            <div className={debtApplicationNote} role="status">
              <ShieldAlert size={17} className={reserveNoteIcon} />
              <span className={debtApplicationBody}>
                <strong className={debtApplicationTitle}>Abatimento de dívida aplicado</strong>
                <span>
                  {formatMoney(debtConsumedAmount.amount, debtConsumedAmount.currency)} deste
                  líquido foi usado para reduzir sua dívida.
                </span>
                <span>
                  Restante a receber:{' '}
                  {formatMoney(fin.remainingReceivable.amount, fin.remainingReceivable.currency)}
                  {hasRemainingReserve
                    ? ` · reserva ainda retida ${formatMoney(remainingReserve.amount, remainingReserve.currency)}`
                    : ''}
                  .
                </span>
              </span>
            </div>
          ) : null}
          <MoneyReconciliation
            total={originalFinancials.netTotal}
            parts={[
              originalFinancials.debtConsumed,
              originalFinancials.remainingReceivable,
              originalFinancials.remainingReserve,
            ]}
          />
          <div className={netRow({ tone: hasFinancialImpact ? 'historical' : 'current' })}>
            <div className={netLead}>
              <span className={netLabel({ tone: hasFinancialImpact ? 'historical' : 'current' })}>
                {hasFinancialImpact ? 'Líquido original da venda' : 'Sua comissão'}
              </span>
              <span className={netNote({ tone: hasFinancialImpact ? 'historical' : 'current' })}>
                {hasFinancialImpact
                  ? 'Antes dos ajustes. Consulte o saldo atual no extrato.'
                  : primaryFinancialEvent
                    ? financialEventNetNote(primaryFinancialEvent.kind)
                    : refund
                      ? 'Confira a recuperação no extrato'
                      : hasDebtConsumption && !hasRemainingReceivable && !hasRemainingReserve
                        ? 'Recebível abatido da dívida: sem data de liberação'
                        : hasDebtConsumption
                          ? 'Parte do líquido foi usada para reduzir sua dívida'
                          : hasRemainingReserve
                            ? 'Inclui reserva; veja as liberações em Recebimentos'
                            : fin.availabilityConfirmed
                              ? 'Disponibilidade confirmada na conta de pagamento'
                              : fin.awaitingAvailability
                                ? 'Aguardando previsão de liberação'
                                : fin.availableDates.length === 1
                                  ? `Liberação prevista: ${customServiceFees ? analyticsDate(fin.availableDates[0]!) : new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(new Date(fin.availableDates[0]!))}`
                                  : fin.availableDates.length > 1
                                    ? `Liberação em ${fin.availableDates.length} datas`
                                    : 'Aguardando previsão de liberação'}
              </span>
            </div>
            <MoneyValue
              amount={fin.netTotal.amount}
              currency={fin.netTotal.currency}
              size="lg"
              className={netValue({ tone: hasFinancialImpact ? 'historical' : 'current' })}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export function SaleRefundConfirmationModal({
  sale,
  target,
  journeyChargeCount,
  open,
  reason,
  loading,
  onReasonChange,
  onCancel,
  onConfirm,
}: {
  sale: SaleDetail
  target: SaleRefundTarget
  journeyChargeCount: number
  open: boolean
  reason: string
  loading: boolean
  onReasonChange: (reason: string) => void
  onCancel: () => void
  onConfirm: () => void
}) {
  const showDebtWarning = isPastDate(target.availableAt)
  const panelRef = useRef<HTMLElement | null>(null)
  const reasonRef = useRef<HTMLTextAreaElement | null>(null)
  const loadingRef = useRef(loading)
  const onCancelRef = useRef(onCancel)
  const showReceivingEquivalent = target.refundAmount.currency !== target.refundGrossAmount.currency
  const isRemainingRefund = target.financialEvents?.some((event) => event.kind === 'PARTIAL_REFUND')
  const otherChargesText =
    journeyChargeCount === 2
      ? 'A outra cobrança continua inalterada.'
      : `As outras ${journeyChargeCount - 1} cobranças continuam inalteradas.`

  useEffect(() => {
    loadingRef.current = loading
  }, [loading])

  useEffect(() => {
    onCancelRef.current = onCancel
  }, [onCancel])

  useEffect(() => {
    if (!open) {
      return
    }
    const previousActive =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    reasonRef.current?.focus()
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !loadingRef.current) {
        onCancelRef.current()
        return
      }
      if (event.key !== 'Tab') {
        return
      }
      const focusable = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((element) => element.tabIndex >= 0)
      if (focusable.length === 0) {
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      if (!panelRef.current?.contains(active)) {
        event.preventDefault()
        first?.focus()
        return
      }
      if (event.shiftKey && active === first) {
        event.preventDefault()
        last?.focus()
        return
      }
      if (!event.shiftKey && active === last) {
        event.preventDefault()
        first?.focus()
      }
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
      previousActive?.focus()
    }
  }, [open])

  if (!open) {
    return null
  }

  return createPortal(
    <div
      className={refundModalBackdrop}
      role="presentation"
      onClick={() => {
        if (!loading) {
          onCancel()
        }
      }}
    >
      <section
        className={refundModalPanel}
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Reembolsar esta cobrança?"
        onClick={(event) => event.stopPropagation()}
      >
        <header className={refundModalHeader}>
          <div>
            <h2 className={refundModalTitle}>Reembolsar esta cobrança?</h2>
            <p className={refundModalTotalLabel}>
              {isRemainingRefund
                ? 'Reembolso do valor restante desta cobrança.'
                : 'Reembolso total desta cobrança, sem reembolso parcial.'}
            </p>
          </div>
          <div className={refundModalTotal}>
            <span className={refundModalTotalLabel}>
              {isRemainingRefund ? 'Total restante' : 'Total reembolsado'}
            </span>
            <span className={refundModalTotalValue}>
              {formatMoney(target.refundAmount.amount, target.refundAmount.currency)}
            </span>
            {showReceivingEquivalent ? (
              <span className={refundModalTotalLabel}>
                Equivalente no painel:{' '}
                {formatMoney(target.refundGrossAmount.amount, target.refundGrossAmount.currency)}
              </span>
            ) : null}
          </div>
        </header>

        <div className={refundModalBody}>
          <dl className={refundModalSummary}>
            <div className={refundModalSummaryItem}>
              <dt className={refundModalSummaryTerm}>Cobrança</dt>
              <dd className={refundModalSummaryValue}>{target.label}</dd>
            </div>
            <div className={refundModalSummaryItem}>
              <dt className={refundModalSummaryTerm}>Produto</dt>
              <dd className={refundModalSummaryValue}>{target.productName}</dd>
            </div>
            <div className={refundModalSummaryItem}>
              <dt className={refundModalSummaryTerm}>Comprador</dt>
              <dd className={refundModalSummaryValue}>{buyerSummary(sale)}</dd>
            </div>
          </dl>

          <label className={refundModalField}>
            <span className={refundModalLabel}>Motivo do reembolso (opcional)</span>
            <textarea
              className={refundModalTextarea}
              ref={reasonRef}
              value={reason}
              maxLength={refundReasonMaxLength}
              disabled={loading}
              onChange={(event) => onReasonChange(event.target.value)}
            />
          </label>

          {journeyChargeCount > 1 ? (
            <p className={refundScopeNote}>
              Este pedido reembolsa somente a cobrança selecionada ({target.label}).{' '}
              {otherChargesText}
            </p>
          ) : null}

          {showDebtWarning ? (
            <div className={refundDebtAlert} role="alert">
              Parte deste valor já foi liberada. Reembolsar agora pode gerar dívida se o valor já
              tiver sido sacado, compensada nas próximas vendas.
            </div>
          ) : null}

          <p className={refundModalIrreversible}>Esta ação não pode ser desfeita.</p>
        </div>

        <footer className={refundModalActions}>
          <Button variant="secondary" disabled={loading} onClick={onCancel}>
            Cancelar
          </Button>
          <Button variant="danger" disabled={loading} onClick={onConfirm}>
            {loading ? 'Confirmando...' : 'Confirmar reembolso'}
          </Button>
        </footer>
      </section>
    </div>,
    document.body,
  )
}

export function SaleDetailDrawer({
  saleId,
  scope = 'journey',
  onClose,
  onBack,
  backLabel,
}: SaleDetailDrawerProps) {
  const detail = useSaleDetailQuery(saleId, scope)
  const requestRefund = useRequestSaleRefundMutation(saleId)
  const [tabState, setTabState] = useState<{
    saleId: string | null
    value: 'summary' | 'composition'
  }>({ saleId, value: 'summary' })
  const [refundTarget, setRefundTarget] = useState<SaleRefundTarget | null>(null)
  const [refundModalSaleId, setRefundModalSaleId] = useState<string | null>(null)
  const [refundReason, setRefundReason] = useState('')
  if (refundModalSaleId !== null && refundModalSaleId !== saleId) {
    setRefundTarget(null)
    setRefundModalSaleId(null)
    setRefundReason('')
  }
  const refundModalOpen = refundTarget !== null && refundModalSaleId === saleId
  const activeTab = tabState.saleId === saleId ? tabState.value : 'summary'
  const drawerPresentation = detail.data ? getJourneyStatusPresentation(detail.data) : null
  const drawerStatuses = detail.data
    ? summarizeJourneyStatuses(saleDetailJourney(detail.data))
    : null
  const DrawerStatusIcon = drawerPresentation?.icon
  const drawerTabs = [
    { id: 'summary', label: 'Resumo' },
    ...(detail.data?.status === 'FAILED' ? [] : [{ id: 'composition', label: 'Valores' }]),
  ]

  function openRefundModal(target: SaleRefundTarget) {
    if (saleId === null) return
    setRefundReason('')
    setRefundModalSaleId(saleId)
    setRefundTarget(target)
  }

  function resetRefundModal() {
    setRefundTarget(null)
    setRefundModalSaleId(null)
    setRefundReason('')
  }

  function closeRefundModal() {
    if (!requestRefund.isPending) {
      resetRefundModal()
    }
  }

  const handleRequestRefund = () => {
    if (refundTarget === null) return
    requestRefund.mutate(
      {
        saleId: refundTarget.saleId,
        ...(refundReason.trim() ? { reason: refundReason.trim() } : {}),
      },
      {
        onSuccess: () => {
          toast.success(`Reembolso de ${refundTarget.label.toLocaleLowerCase('pt-BR')} solicitado.`)
          resetRefundModal()
        },
        onError: (error) => {
          toast.error(
            getUserFacingError(error, {
              title: 'Não foi possível solicitar o reembolso',
              fallback: 'Revise os dados da solicitação e tente novamente.',
            }),
          )
        },
      },
    )
  }

  function closeDrawer() {
    resetRefundModal()
    onClose()
  }

  return (
    <>
      <Drawer
        open={saleId !== null}
        onClose={closeDrawer}
        onBack={onBack}
        backLabel={backLabel}
        title={detail.data?.productName ?? 'Detalhe da venda'}
        description={
          detail.data
            ? [detail.data.buyerName, detail.data.buyerEmail].filter(Boolean).join(' · ')
            : 'Resumo da compra e situação das cobranças.'
        }
        size="wide"
        variant="financial"
        busy={requestRefund.isPending}
        status={
          drawerPresentation && DrawerStatusIcon && detail.data ? (
            <div className={detailBadgeRow}>
              <StatusBadge
                tone={drawerPresentation.tone}
                label={drawerPresentation.label}
                icon={<DrawerStatusIcon size={14} />}
              />
              {drawerStatuses && drawerStatuses.chargeCount > 1 ? (
                <span className={chargeTag}>{drawerStatuses.label}</span>
              ) : null}
              {detail.data.globalMarket ? (
                <span className={globalMarketTag}>
                  <Globe2 size={13} />
                  Mercado Global
                </span>
              ) : null}
              <span className={chargeTag}>
                {detail.data.isRenewal
                  ? `Renovação${detail.data.subscriptionCycleN ? ` · ciclo ${detail.data.subscriptionCycleN}` : ''}`
                  : detail.data.subscriptionId
                    ? 'Recorrente'
                    : 'Pagamento único'}
              </span>
            </div>
          ) : undefined
        }
        tabs={
          detail.data ? (
            <DrawerTabs
              tabs={drawerTabs}
              value={activeTab}
              onChange={(value) =>
                setTabState({
                  saleId,
                  value: value as 'summary' | 'composition',
                })
              }
              ariaLabel="Seções do detalhe da venda"
            />
          ) : undefined
        }
      >
        {detail.isError ? (
          <ErrorState
            onRetry={() => {
              void detail.refetch()
            }}
          />
        ) : detail.isPending || !detail.data ? (
          <div className={detailClass}>
            <Skeleton height={120} />
            <Skeleton height={160} />
          </div>
        ) : (
          <SaleDetailBody
            key={detail.data.id}
            sale={detail.data}
            activeTab={activeTab}
            onRequestRefund={openRefundModal}
            requestingRefund={requestRefund.isPending}
          />
        )}
      </Drawer>
      {detail.data && refundTarget && refundModalOpen ? (
        <SaleRefundConfirmationModal
          sale={detail.data}
          target={refundTarget}
          journeyChargeCount={refundTargetsFor(detail.data).length}
          open={refundModalOpen}
          reason={refundReason}
          loading={requestRefund.isPending}
          onReasonChange={setRefundReason}
          onCancel={closeRefundModal}
          onConfirm={handleRequestRefund}
        />
      ) : null}
    </>
  )
}
